import React from 'react';
import {Button, Col, Label, Row} from 'reactstrap';
import {AvForm, AvGroup} from 'availity-reactstrap-validation';
import {Fieldset} from 'primereact/fieldset';
import Translate from '../../shared/language/Translate';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import AvField from '../../shared/component/form/AvField';
import PurchaseRequisitionService from '../../shared/service/PurchaseRequisitionService';
import PurchaseRequisitionItemMobileListColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionItemMobileListColumn.json';
import Dialog from '../../shared/component/dialog/Dialog';
import Loading from '../../shared/component/others/Loading';
import PurchaseRequisitionItemMobileSave from './PurchaseRequisitionItemMobileSave';
import PurchaseRequisitionItemDataTable from '../purchaserequisition/PurchaseRequisitionItemDataTable';
import {applyCostWarningsToItems, normalizeItem, normalizeRequisition, persistRequisitionToDb, upsertRequisitionItem} from '../purchaserequisition/PurchaseRequisitionUtils';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import '../../shared/styles/SettingsPage.css';

const defaultRequisition = {
    id: null,
    description: '',
    requisitionStatus: 'OPEN',
    createDate: null,
    itemList: []
};

const defaultItem = {
    id: null,
    barcode: null,
    itemName: null,
    itemBssId: null,
    itemUnitMeasure: null,
    itemSize: null,
    boxSize: null,
    deliveredBoxCount: null,
    supplierId: null,
    supplierName: null,
    supplierBssId: null,
    currentCost: null,
    minStock: null,
    maxStock: null,
    recommendedOrderAmount: null,
    actualOrderAmount: null,
    damagedQuantity: null,
    expiryDate: null,
    currentStock: null,
    status: 'ACTIVE'
};

export default class PurchaseRequisitionSaveMobile extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            requisition: Object.assign({}, defaultRequisition),
            loading: false,
            displayDialog: false
        };
        this.purchaseRequisitionService = new PurchaseRequisitionService();
        this.dialogHistoryPushed = false;
        this.ignoreNextPopState = false;
        this.handlePopState = this.handlePopState.bind(this);
        this.handleBackButton = this.handleBackButton.bind(this);
    }

    componentDidMount() {
        if (!GeneralUtils.isMobile()) {
            window.location.href = '#';
            return;
        }
        window.history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', this.handlePopState);
        window.__purchaseRequisitionMobileBack = this.handleBackButton;

        const id = this.getRequisitionIdFromUrl();
        if (!GeneralUtils.__isValueEmpty(id) && id !== '-1') {
            this.loadRequisition(id);
        } else {
            this.initNewRequisition();
        }
    }

    getRequisitionIdFromUrl = () => {
        const query = window.location.href.split('?')[1];
        if (GeneralUtils.__isValueEmpty(query)) {
            return null;
        }
        const idPart = query.split('&')[0];
        if (!GeneralUtils.__isValueEmpty(idPart) && idPart !== '-1') {
            return idPart;
        }
        return null;
    };

    isDeliveryModeFromUrl = () => {
        const query = window.location.href.split('?')[1] || '';
        return query.includes('mode=delivery');
    };

    isDeliveryNote = () => this.state.requisition?.requisitionStatus === 'DELIVERY_NOTE';

    isApproved = () => {
        const approved = this.state.requisition?.approved;
        return approved === true || approved === 'true' || approved === 1 || approved === '1';
    };

    isEditable = () => {
        if (this.isApproved()) {
            return false;
        }
        const status = this.state.requisition?.requisitionStatus;
        return status === 'OPEN' || status === 'DELIVERY_NOTE';
    };

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handlePopState);
        if (window.__purchaseRequisitionMobileBack === this.handleBackButton) {
            delete window.__purchaseRequisitionMobileBack;
        }
    }

    initNewRequisition = () => {
        const isDelivery = this.isDeliveryModeFromUrl();
        this.setState({
            requisition: {
                ...defaultRequisition,
                requisitionStatus: isDelivery ? 'DELIVERY_NOTE' : 'OPEN',
                createDate: dayjs().format('DD/MM/YYYY'),
                itemList: []
            }
        });
    };

    loadRequisition = (id) => {
        this.setState({loading: true});
        this.purchaseRequisitionService.findById(id).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                const requisition = Object.assign({}, response);
                if (requisition.createDate) {
                    requisition.createDate = dayjs(requisition.createDate).format('DD/MM/YYYY');
                }
                if (requisition.checkoutDate) {
                    requisition.checkoutDate = dayjs(requisition.checkoutDate).format('DD/MM/YYYY');
                }
                requisition.itemList = (requisition.itemList || []).map(normalizeItem);
                this.applyCostWarnings(requisition).then(enriched => {
                    this.setState({requisition: enriched, loading: false});
                });
            } else {
                this.setState({loading: false});
            }
        }).catch(() => this.setState({loading: false}));
    };

    applyCostWarnings = (requisition) => {
        if (!requisition?.id || requisition.requisitionStatus !== 'OPEN') {
            return Promise.resolve(requisition);
        }
        return this.purchaseRequisitionService.getCostWarnings(requisition.id).then(warnings => {
            if (GeneralUtils.isNullOrEmpty(warnings) || !GeneralUtils.isNullOrEmpty(warnings.type)) {
                return requisition;
            }
            return {
                ...requisition,
                itemList: applyCostWarningsToItems(requisition.itemList, warnings)
            };
        }).catch(() => requisition);
    };

    hasSavedRequisition = () => !GeneralUtils.isNullOrEmpty(this.state.requisition?.id);

    getItemListFields = () => {
        const isDelivery = this.isDeliveryNote();
        return PurchaseRequisitionItemMobileListColumn.fields
            .filter(field => {
                if (isDelivery) {
                    return ['barcode', 'itemNameWithSizeAndQuantity', 'boxSize', 'deliveredBoxCount', 'actualOrderAmount', 'damagedQuantity', 'expiryDate'].includes(field.field);
                }
                return !['damagedQuantity', 'expiryDate', 'deliveredBoxCount', 'itemUnitMeasure', 'boxSize'].includes(field.field)
                    && field.visible !== false;
            })
            .map(field => {
                if (isDelivery && field.field === 'itemNameWithSizeAndQuantity') {
                    return {...field, field: 'itemName', header: 'Name', visible: true};
                }
                if (isDelivery && field.field === 'boxSize') {
                    return {...field, header: 'Units per Box', visible: true};
                }
                if (isDelivery && field.field === 'deliveredBoxCount') {
                    return {...field, header: 'Number of Boxes', visible: true};
                }
                if (isDelivery && field.field === 'damagedQuantity') {
                    return {...field, header: 'Waste Quantity', visible: true};
                }
                if (isDelivery && field.field === 'actualOrderAmount') {
                    return {...field, header: 'Total Quantity', visible: true};
                }
                if (isDelivery && field.field === 'expiryDate') {
                    return {...field, visible: true};
                }
                return field;
            });
    };

    saveRequisition = () => {
        if (!this.isEditable()) {
            return;
        }
        const requisition = {...this.state.requisition};
        const itemList = Array.isArray(requisition.itemList) ? requisition.itemList : [];

        if (itemList.length === 0) {
            toast.warn('Please add at least one item!');
            return;
        }

        this.setState({loading: true});
        persistRequisitionToDb(requisition, this.purchaseRequisitionService).then(result => {
            if (!result.success) {
                this.setState({loading: false});
                return;
            }
            const saved = result.requisition;
            const hadId = !GeneralUtils.isNullOrEmpty(requisition.id);
            this.setState({requisition: saved, loading: false}, () => {
                if (!hadId && saved.id) {
                    const modeSuffix = this.isDeliveryNote() ? '&mode=delivery' : '';
                    window.history.replaceState(null, document.title, '#purchaseRequisitionSaveMobile?' + saved.id + modeSuffix);
                }
            });
        });
    };

    approveRequisition = () => {
        if (!this.isDeliveryNote() || !this.isEditable() || !this.hasSavedRequisition()) {
            return;
        }
        const itemList = this.state.requisition.itemList || [];
        if (itemList.length === 0) {
            toast.warn('Please add at least one item!');
            return;
        }
        if (!window.confirm('Are you sure to approve? Delivery Note will be locked (Closed).')) {
            return;
        }
        const id = this.state.requisition.id;
        this.setState({loading: true});
        this.purchaseRequisitionService.approve(id).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                this.purchaseRequisitionService.findById(id).then(reloaded => {
                    if (!GeneralUtils.isNullOrEmpty(reloaded) && GeneralUtils.isNullOrEmpty(reloaded.type)) {
                        this.setState({
                            requisition: normalizeRequisition({...reloaded, approved: reloaded.approved || true}),
                            loading: false
                        });
                    } else {
                        this.setState(prev => ({
                            requisition: {...prev.requisition, approved: true},
                            loading: false
                        }));
                    }
                }).catch(() => this.setState(prev => ({
                    requisition: {...prev.requisition, approved: true},
                    loading: false
                })));
                return;
            }
            this.setState({
                requisition: normalizeRequisition({...response, approved: true}),
                loading: false
            });
        }).catch(() => this.setState(prev => ({
            requisition: {...prev.requisition, approved: true},
            loading: false
        })));
    };

    onDataSelect = (data) => {
        if (!this.isEditable()) {
            return;
        }
        this.pushDialogHistory();
        GeneralUtils.openDialogThenRef(this, {displayDialog: true}, () => this.refModelSave, (ref) => ref.setData(data));
    };

    addNewItem = () => {
        if (!this.isEditable()) {
            return;
        }
        const item = Object.assign({}, defaultItem);
        if (this.isDeliveryNote()) {
            item.deliveredBoxCount = 1;
        }
        this.onDataSelect(item);
    };

    persistItemAndContinue = (itemData, closeAfterSave = true) => {
        const {itemList} = upsertRequisitionItem(this.state.requisition.itemList, itemData);
        const requisition = {...this.state.requisition, itemList};
        const hadRequisitionId = !GeneralUtils.isNullOrEmpty(requisition.id);

        this.setState({loading: true});
        persistRequisitionToDb(requisition, this.purchaseRequisitionService).then(result => {
            if (!result.success) {
                this.setState({loading: false});
                return;
            }
            this.applyCostWarnings(result.requisition).then(enriched => {
                this.setState({requisition: enriched, loading: false}, () => {
                    if (!hadRequisitionId && enriched.id) {
                        const modeSuffix = this.isDeliveryNote() ? '&mode=delivery' : '';
                        window.history.replaceState(null, document.title, '#purchaseRequisitionSaveMobile?' + enriched.id + modeSuffix);
                    }
                    if (closeAfterSave) {
                        this.closeItemDialog();
                        return;
                    }
                    const nextItem = Object.assign({}, defaultItem);
                    if (this.isDeliveryNote()) {
                        nextItem.deliveredBoxCount = 1;
                    }
                    GeneralUtils.openDialogThenRef(
                        this,
                        {displayDialog: true},
                        () => this.refModelSave,
                        (ref) => ref.setData(nextItem)
                    );
                });
            });
        });
    };

    saveItem = (itemData) => this.persistItemAndContinue(itemData, true);

    saveItemAndNext = (itemData) => this.persistItemAndContinue(itemData, false);

    closeItemDialog = () => {
        this.setState({displayDialog: false});
        this.popDialogHistory();
    };

    __handleChange = (e) => {
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setState(prev => ({
            requisition: {...prev.requisition, [e.target.name]: value}
        }));
    };

    handleBackButton = () => {
        if (this.state.displayDialog) {
            this.closeItemDialog();
            return true;
        }
        return false;
    };

    renderButtons() {
        const editable = this.isEditable();
        const isDelivery = this.isDeliveryNote();
        return (
            <div className="mobile-fixed-footer">
                <div className="mobile-fixed-footer-inner">
                    {editable && isDelivery &&
                        <Button
                            color="outline-primary"
                            style={{marginRight: '5px', height: '30px', width: '110px'}}
                            onClick={this.approveRequisition}
                            disabled={!this.hasSavedRequisition()}
                        >
                            Approve
                        </Button>
                    }
                    {editable &&
                        <Button
                            color="outline-primary"
                            style={{marginRight: '5px', height: '30px', width: '110px'}}
                            onClick={this.saveRequisition}
                        >
                            <Translate contentKey="entity.action.add">Save</Translate>
                        </Button>
                    }
                    <Button
                        color="outline-primary"
                        className="settings-page__btn--cancel settings-page__btn--back"
                        style={{marginRight: '5px', height: '30px', minWidth: '110px'}}
                        onClick={() => { window.location.href = '#purchaseRequisitionMobile'; }}
                    >
                        <i className="pi pi-arrow-left" aria-hidden="true"/>
                        <Translate contentKey="entity.action.cancel">Back</Translate>
                    </Button>
                </div>
            </div>
        );
    }

    render() {
        if (!GeneralUtils.isMobile()) {
            return null;
        }

        const items = this.state.requisition.itemList || [];
        const isDelivery = this.isDeliveryNote();

        return (
            <div className="mobile-page-content">
                <AvForm autocomplete="off" onSubmit={(e) => e.preventDefault()}>
                    <Fieldset legend={isDelivery ? 'Delivery Note Detail' : 'Requisition Detail'}>
                        <Row>
                            <Col md="6">
                                <AvGroup>
                                    <Label for="requisitionNo">
                                        {isDelivery ? 'Delivery Note No' : 'Requisition No'}
                                    </Label>
                                    <AvField
                                        type="text"
                                        name="requisitionNo"
                                        disabled={true}
                                        value={this.state.requisition.requisitionNo || ''}
                                    />
                                </AvGroup>
                            </Col>
                            <Col md="6">
                                <AvGroup>
                                    <Label for="createDate">
                                        <Translate contentKey="merchantBranch.updateDate">Create Date</Translate>
                                    </Label>
                                    <AvField
                                        type="text"
                                        name="createDate"
                                        disabled={true}
                                        value={this.state.requisition.createDate || ''}
                                    />
                                </AvGroup>
                            </Col>
                        </Row>
                        <Row>
                            {!isDelivery &&
                                <Col md="6">
                                    <AvGroup>
                                        <Label for="checkoutDate">Checkout Date</Label>
                                        <AvField
                                            type="text"
                                            name="checkoutDate"
                                            disabled={true}
                                            value={this.state.requisition.checkoutDate || ''}
                                        />
                                    </AvGroup>
                                </Col>
                            }
                            <Col md={isDelivery ? '12' : '6'}>
                                <AvGroup>
                                    <Label for="description">
                                        <Translate contentKey="purchaseOrderItem.itemName">Description</Translate>
                                    </Label>
                                    <AvField
                                        type="text"
                                        name="description"
                                        disabled={!this.isEditable()}
                                        value={this.state.requisition.description || ''}
                                        onChange={this.__handleChange}
                                    />
                                </AvGroup>
                            </Col>
                        </Row>
                    </Fieldset>

                    <Fieldset legend="Items">
                        {this.isEditable() &&
                            <div style={{marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end', marginBottom: '8px'}}>
                                <Button
                                    style={{
                                        height: '30px',
                                        width: '30px',
                                        padding: 0,
                                        border: 'none',
                                        boxShadow: 'none',
                                        background: 'transparent'
                                    }}
                                    onClick={this.addNewItem}
                                >
                                    <i className="pi pi-plus" style={{fontSize: '30px', color: '#1982D9'}}/>
                                </Button>
                            </div>
                        }

                        <Dialog
                            visible={this.state.displayDialog}
                            onHide={this.closeItemDialog}
                            className="full-screen-dialog"
                            appendTo={document.body}
                            modal={true}
                            draggable={false}
                            resizable={false}
                        >
                            <PurchaseRequisitionItemMobileSave
                                ref={(elem) => this.refModelSave = elem}
                                deliveryMode={isDelivery}
                                showCostAlternatives={false}
                                saveItem={this.saveItem}
                                saveItemAndNext={this.saveItemAndNext}
                                closeItemDialog={this.closeItemDialog}
                            />
                        </Dialog>

                        <PurchaseRequisitionItemDataTable
                            data={items}
                            selectionMode={this.isEditable() ? 'single' : null}
                            paginator={true}
                            rows={50}
                            onDataSelect={this.onDataSelect}
                            fields={this.getItemListFields()}
                        />
                    </Fieldset>

                    {this.renderButtons()}
                </AvForm>
                {this.state.loading && <Loading/>}
            </div>
        );
    }

    pushDialogHistory = () => {
        if (!this.dialogHistoryPushed) {
            window.history.pushState({dialog: true}, document.title, window.location.href);
            this.dialogHistoryPushed = true;
        }
    };

    popDialogHistory = () => {
        if (this.dialogHistoryPushed) {
            this.ignoreNextPopState = true;
            window.history.back();
            this.dialogHistoryPushed = false;
        }
    };

    handlePopState() {
        if (this.ignoreNextPopState) {
            this.ignoreNextPopState = false;
            return;
        }
        if (this.state.displayDialog) {
            this.setState({displayDialog: false}, () => {
                this.dialogHistoryPushed = false;
                window.history.pushState(null, document.title, window.location.href);
            });
        }
    }
}
